import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Grid, FormHelperText } from '@mui/material';
import { Button, StyledCardSection } from '@fnol/library/src/components';
import { GenericObject, getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { personType, riskTypeLife, translations } from '../../lib/consts';
import { useValidateIndividual } from '../../lib/customHooks';
import Individual, { IndividualCleanup } from '../Individual/Individual';
import UsersTable from '../UsersTable';
import AMLSection from '../AMLSection';
import { AMLSectionCleanup } from '../AMLSection/AMLSection';
import { scrollToFirstError } from '../../lib/helpers';

const UsersAddPerson = () => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<GenericObject>();
    const [inputPersonId, setInputPersonId] = useState(0);
    const [addPossible, setAddPossible] = useState(true);
    const individualValidation = useValidateIndividual(personType.user);

    useEffect(() => {
        setFieldValue('inputPersonOpen', false);
    }, []);

    const checkNextUserPersonId = () => {
        const availableIds = [1, 2, 3, 4, 5];
        let nextAvailableId = 0;
        availableIds.map((availableId) => {
            if (!values[`user${availableId}Name`] && nextAvailableId === 0) {
                nextAvailableId = availableId;
            }
        });
        return nextAvailableId;
    };

    const saveUserPersonToId = (idToSaveTo: number) => {
        if (individualValidation.isValid) {
            setTouched(
                {
                    userIndividualUCN: true,
                },
                false,
            );

            setFieldValue(`user${idToSaveTo}Name`, values.userName);
            setFieldValue(`user${idToSaveTo}LastName`, values.userLastName);
            setFieldValue(`user${idToSaveTo}DateOfBirth`, values.userDateOfBirth);
            setFieldValue(`user${idToSaveTo}UCN`, values.userUCN);
            setFieldValue(`user${idToSaveTo}Citizenship`, values.userCitizenship);
            setFieldValue(`user${idToSaveTo}DualCitizenship`, values.userDualCitizenship);
            setFieldValue(`user${idToSaveTo}Country`, values.userCountry);
            setFieldValue(`user${idToSaveTo}MyCountry`, values.userMyCountry);
            setFieldValue(`user${idToSaveTo}City`, values.userCity);
            setFieldValue(`user${idToSaveTo}CityInSerbia`, values.userCityInSerbia);
            setFieldValue(`user${idToSaveTo}ZipCode`, values.userZipCode);
            setFieldValue(`user${idToSaveTo}Street`, values.userStreet);
            setFieldValue(`user${idToSaveTo}Phone`, values.userPhone);
            setFieldValue(`user${idToSaveTo}Email`, values.userEmail);
            setFieldValue(`user${idToSaveTo}PersonalDocumentType`, values.userPersonalDocumentType);
            setFieldValue(`user${idToSaveTo}PersonalDocumentNumber`, values.userPersonalDocumentNumber);
            setFieldValue(`user${idToSaveTo}DocumentIssuePlace`, values.userDocumentIssuePlace);
            setFieldValue(`user${idToSaveTo}DocumentIssueDate`, values.userDocumentIssueDate);
            setFieldValue(`user${idToSaveTo}PlaceOfBirth`, values.userPlaceOfBirth);
            setFieldValue(`user${idToSaveTo}Gender`, values.userGender);
            setFieldValue(`user${idToSaveTo}Municipality`, values.userMunicipality);
            setFieldValue(`user${idToSaveTo}CurrentResidenceAddress`, values.userCurrentResidenceAddress);
            setFieldValue(`user${idToSaveTo}CurrentResidenceStreet`, values.userCurrentResidenceStreet);
            setFieldValue(`user${idToSaveTo}CurrentResidenceMunicipality`, values.userCurrentResidenceMunicipality);
            setFieldValue(`user${idToSaveTo}CurrentResidenceCity`, values.userCurrentResidenceCity);
            setFieldValue(`user${idToSaveTo}CurrentResidenceCityInSerbia`, values.userCurrentResidenceCityInSerbia);
            setFieldValue(`user${idToSaveTo}CurrentResidenceZipCode`, values.userCurrentResidenceZipCode);
            setFieldValue(`user${idToSaveTo}MailingAddress`, values.userMailingAddress);
            setFieldValue(`user${idToSaveTo}MailingStreet`, values.userMailingStreet);
            setFieldValue(`user${idToSaveTo}MailingMunicipality`, values.userMailingMunicipality);
            setFieldValue(`user${idToSaveTo}MailingCity`, values.userMailingCity);
            setFieldValue(`user${idToSaveTo}MailingCityInSerbia`, values.userMailingCityInSerbia);
            setFieldValue(`user${idToSaveTo}MailingZipCode`, values.userMailingZipCode);
            setFieldValue(`user${idToSaveTo}Statement`, values.userStatement);
            setFieldValue(`user${idToSaveTo}StatementNote`, values.userStatementNote);
            if (getIsSalesForce(getApplicationId())) {
                setFieldValue(`user${idToSaveTo}AttorneySignature`, values.userAttorneySignature);
            }

            if (idToSaveTo === 5) {
                setAddPossible(false);
            }
            IndividualCleanup(personType.user, setFieldValue);
            AMLSectionCleanup(personType.user, setFieldValue);
            setInputPersonId(0);
            setFieldValue('inputPersonOpen', false);
        }
    };

    const handleCancel = () => {
        IndividualCleanup(personType.user, setFieldValue);
        AMLSectionCleanup(personType.user, setFieldValue);
        setInputPersonId(0);
        setFieldValue('inputPersonOpen', false);
    };

    const handleSave = (idToEdit: number) => {
        setTouched(
            {
                ...individualValidation.touched,
            },
            false,
        );
        setErrors(individualValidation.errors);
        scrollToFirstError(individualValidation.errors);

        if (individualValidation.isValid) {
            const availableId = checkNextUserPersonId();
            if (idToEdit === 0 && availableId !== 0) {
                saveUserPersonToId(availableId);
            } else {
                if (idToEdit !== 0) {
                    saveUserPersonToId(idToEdit);
                } else {
                    return false;
                }
            }
        }
    };

    useEffect(() => {
        const nextAvailableId = checkNextUserPersonId();
        if (nextAvailableId === 0) {
            setAddPossible(false);
        }
    }, [addPossible]);

    return (
        <>
            <>
                <UsersTable
                    inputPersonOpen={values.inputPersonOpen}
                    setInputPersonId={setInputPersonId}
                    setAddPossible={setAddPossible}
                />
                {(values.risks.includes(riskTypeLife.death) ||
                    values.risks.includes(riskTypeLife.deathByAccident) ||
                    values.risks.includes(riskTypeLife.deathByTrafficAccident)) && (
                    <Grid container justifyContent="center">
                        <Button
                            onClickHandler={() => setFieldValue('inputPersonOpen', true)}
                            label={translations.users.ADD_NEW}
                            disabled={values.inputPersonOpen || !addPossible}
                            fullWidth={false}
                        />
                    </Grid>
                )}
            </>

            {!values.submittedSuccessfully && values.submitError !== '' ? (
                <FormHelperText error={true}>
                    <>{values.submitError}</>
                </FormHelperText>
            ) : (
                <></>
            )}

            {values.inputPersonOpen && (
                <>
                    <StyledCardSection>
                        <Individual type={personType.user} />
                        {getIsSalesForce(getApplicationId()) && <AMLSection type={personType.user}></AMLSection>}
                    </StyledCardSection>

                    <Grid container>
                        <Grid container item={true} xs={6}>
                            <Button onClickHandler={handleCancel} label={translations.users.CANCEL} fullWidth={false} />
                        </Grid>
                        <Grid container item={true} xs={6} justifyContent="flex-end">
                            <Button
                                onClickHandler={() => handleSave(inputPersonId)}
                                label={translations.users.SAVE}
                                fullWidth={false}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </>
    );
};

export default UsersAddPerson;
